import { supabase } from "@/integrations/supabase/client";
import type { TablesInsert, Json } from "@/integrations/supabase/types";
import { logger } from "@/services/LoggingService";

export type Persona = "investor" | "advisor" | "asset_manager";

export interface IntakeAnswers {
  persona?: Persona;
  name?: string;
  country?: string;
  citizenship?: string;
  default_currency?: string; // ISO code
  goals?: string[];
  goals_other?: string;
  investment_styles?: string[];
  seeking_clients?: string;
  aum?: string;
  advisor_credentials?: string[];
  referral?: string;
  referral_source?: string;
  linkedin_profile?: string;
}

export async function getSignupEnabled(): Promise<boolean> {
  const { data, error } = await supabase
    .from("system_settings")
    .select("signup_enabled")
    .limit(1)
    .maybeSingle();
  if (error) {
    logger.warn("intakeService.getSignupEnabled", "fallback TRUE", { error: error.message });
    return true;
  }
  return data?.signup_enabled ?? true;
}

export async function upsertIntakeResponses(params: {
  email: string;
  user_id?: string | null;
  answers: IntakeAnswers;
  version?: string;
}): Promise<void> {
  const { email, user_id, answers, version = "v1" } = params;
  const payload: TablesInsert<"user_intake_responses"> = {
    email,
    user_id: user_id ?? null,
    answers: (answers as unknown) as Json,
    version,
    updated_at: new Date().toISOString(),
  };
  const { error } = await supabase
    .from("user_intake_responses")
    .upsert(payload, { onConflict: "email,version" });
  if (error) throw error;
}

export async function updateUserProfileOnboarding(userId: string, update: {
  persona?: Persona;
  professional_designations?: string[];
  country?: string;
  citizenship?: string;
  default_currency?: string;
  linkedin_profile?: string;
  welcome_seen_at?: string;
  first_name?: string | null;
  last_name?: string | null;
  role?: 'investor' | 'advisor' | 'manager' | 'manager_admin' | 'sys_admin';
}): Promise<void> {
  const payload = {
    ...(update.persona && { persona: update.persona }),
    ...(update.professional_designations !== undefined && { professional_designations: update.professional_designations }),
    ...(update.country && { country: update.country }),
    ...(update.citizenship && { citizenship: update.citizenship }),
    ...(update.default_currency && { default_currency: update.default_currency }),
    ...(update.linkedin_profile && { linkedin_profile: update.linkedin_profile }),
    ...(update.first_name !== undefined && { first_name: update.first_name }),
    ...(update.last_name !== undefined && { last_name: update.last_name }),
    ...(update.role && { role: update.role }),
    welcome_seen_at: update.welcome_seen_at ?? new Date().toISOString(),
  } as const;

  logger.info("intakeService.updateUserProfileOnboarding", "update.start", { userId, keys: Object.keys(payload) });
  const { error } = await supabase
    .from("user_profiles")
    .update(payload)
    .eq("id", userId);
  if (error) {
    logger.error("intakeService.updateUserProfileOnboarding", "update.error", { userId, error: error.message });
    throw error;
  }
  logger.info("intakeService.updateUserProfileOnboarding", "update.done", { userId });
}

export async function ensureWaitlistByEmail(email: string, provider?: {
  provider_type?: string;
  provider_id?: string;
  provider_data?: any;
  first_name?: string | null;
  last_name?: string | null;
  avatar_url?: string | null;
}): Promise<string | null> {
  // Tries to call RPC add_to_waitlist if present; otherwise returns null
  try {
    const { data, error } = await supabase.rpc("add_to_waitlist", {
      user_email: email,
      provider_type_param: provider?.provider_type ?? "email",
      provider_id_param: provider?.provider_id ?? email,
      provider_data_param: provider?.provider_data ?? null,
      first_name_param: provider?.first_name ?? null,
      last_name_param: provider?.last_name ?? null,
      avatar_url_param: provider?.avatar_url ?? null,
    });
    if (error) {
      logger.warn("intakeService.ensureWaitlistByEmail", "rpc failed", { error: error.message });
      return null;
    }
    return data as string | null;
  } catch (e: any) {
    logger.warn("intakeService.ensureWaitlistByEmail", "rpc exception", { error: String(e?.message || e) });
    return null;
  }
}

export async function updateWaitlistOnboarding(email: string, update: {
  persona?: Persona;
  professional_designations?: string[];
  country?: string;
  citizenship?: string;
  default_currency?: string;
  onboarding_responses?: any;
  welcome_seen_at?: string;
  full_name?: string | null;
}): Promise<void> {
  const payload = {
    ...(update.persona && { persona: update.persona }),
    ...(update.professional_designations !== undefined && { professional_designations: update.professional_designations }),
    ...(update.country && { country: update.country }),
    ...(update.citizenship && { citizenship: update.citizenship }),
    ...(update.default_currency && { default_currency: update.default_currency }),
    ...(update.onboarding_responses && { onboarding_responses: update.onboarding_responses }),
    ...(update.full_name !== undefined && { full_name: update.full_name }),
    welcome_seen_at: update.welcome_seen_at ?? new Date().toISOString(),
  } as const;

  logger.info("intakeService.updateWaitlistOnboarding", "update.start", { email, keys: Object.keys(payload) });
  const { error } = await supabase
    .from("user_waitlist")
    .update(payload)
    .eq("email", email);
  if (error) {
    logger.error("intakeService.updateWaitlistOnboarding", "update.error", { email, error: error.message });
    throw error;
  }
  logger.info("intakeService.updateWaitlistOnboarding", "update.done", { email });
}
